<template>
  <div class="blogFilters" id="blogFilters">
    <div :class="[{ container: contentType !== 'big-news' }]">
      <div class="blogFilters__title" v-if="title">{{ title }}</div>
      <div class="blogFilters__itemsWrapper">
        <template v-if="routeName === 'blog'">
          <div class="blogFilters__header" v-if="sectionTitle">
            <h2 class="blogFilters__sectionTitle">
              {{ sectionTitle }}
            </h2>
          </div>
          <blog-main-article
            :items="items"
            v-if="pagination.currentPage === 1 && !$route.query.query"
          />
        </template>
        <div :class="[{ 'blogFilters__items--loading': isLoading }]" class="blogFilters__items">
          <template v-if="items.length">
            <div
              :key="article.id"
              :class="['blogFilters__item--big', 'blogFilters__item']"
              v-for="article in pagination.currentPage === 1 &&
              !$route.query.query &&
              routeName === 'blog'
                ? items.slice(1)
                : items"
            >
              <blog-large-tile v-if="items.length" :item="article" lazy />
            </div>
          </template>
          <template v-else>
            <info-bar
              :with-close="false"
              color="#ff6e00"
              icon="warning"
              message="Przepraszamy, nie znaleźliśmy wyników."
            />
          </template>
        </div>
        <loader v-if="isLoading" />
      </div>
      <div class="blogFilters__pagination">
        <pagination
          use-href
          :route-name="routeName"
          :route-pagination-name="routePaginationName"
          :pagination="pagination"
          @change="changePage"
          center
          class="searchTemplate__resultPagination"
          light-version
          scroll-id="blogFilters"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';
import BlogTile from '~/components/Blog/BlogTile';
import BlogLargeTile from '~/components/Blog/BlogLargeTile';
import Pagination from '~/components/Pagination';
import DropdownFilter from '~/components/DropdownFilter';
import InfoBar from '~/components/InfoBar';
import Dropdown from '~/components/Dropdown';
import Loader from '~/components/Loader';
import BlogMainArticle from '~/components/Blog/BlogMainArticle';

export default {
  name: 'BlogFilters',
  components: {
    BlogTile,
    Pagination,
    Dropdown,
    DropdownFilter,
    BlogLargeTile,
    Loader,
    InfoBar,
    BlogMainArticle,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    additionalOffset: {
      type: Number,
      default: 0,
    },
    filtersType: {
      type: String,
      default: 'author',
    },
    routeName: {
      type: String,
      default: '',
    },
    routePaginationName: {
      type: String,
      default: '',
    },
    contentType: {
      type: String,
      default: 'news',
    },
    sectionTitle: {
      type: String,
      default: 'Artykuły i porady',
    },
  },
  data() {
    return {
      isShowListFilter: false,
    };
  },
  async fetch() {
    if (this.$route.query.query) {
      this.setQuery(this.$route.query.query);
    }
    this.setAdditionalOffset(this.additionalOffset);
    if (this.filtersType === 'author') {
      this.setAuthorSlug({ value: this.$route.params.slug });
    }
    if (this.filtersType === 'category') {
      this.setCategorySlug({ value: this.$route.params.slug });
    }

    if (this.$route.params.number) {
      const offset = this.$route.query.limit ? this.$route.query.limit : this.pagination.limit;
      this.setPagination({
        value: {
          limit: this.$route.query.limit ? this.$route.query.limit : 12,
          offset: (parseInt(this.$route.params.number) - 1) * parseInt(offset),
          countTotal: 0,
          currentPage: this.$route.params.number,
        },
      });
    }
    await this.fetchData();
  },
  computed: {
    ...mapState({
      limitFilters: state => state.news.limitFilters,
      items: state => state.news.items,
      sort: state => state.news.sort,
      pagination: state => state.news.pagination,
      isLoading: state => state.news.isLoading,
      query: state => state.news.filters.query,
    }),
    filterSortValue() {
      return this.sortFilters.find(x => x.active === true);
    },
  },
  destroyed() {
    this.resetState();
  },
  methods: {
    ...mapActions({
      fetchData: 'news/fetchData',
      changePagination: 'news/changePagination',
    }),
    ...mapMutations({
      resetState: 'news/resetState',
      setCategorySlug: 'news/setCategorySlug',
      setAuthorSlug: 'news/setAuthorSlug',
      setPagination: 'news/setPagination',
      setAdditionalOffset: 'news/setAdditionalOffset',
      setQuery: 'news/setQuery',
    }),
    changePage(pageNum) {
      if (pageNum < 2) {
        history.pushState({}, null, this.localePath({ name: this.routeName }));
      } else {
        history.pushState(
          {},
          null,
          this.localePath({ name: this.routePaginationName, params: { number: pageNum } }),
        );
      }
      this.changePagination({
        value: {
          ...this.pagination,
          offset: (parseInt(pageNum) - 1) * this.pagination.limit,
          currentPage: pageNum,
        },
      });
    },
    onLimitChange(val) {
      this.$helper.scrollToId('blogFilters');
      history.pushState({}, null, this.localePath({ name: this.routeName }));
      this.changePagination({
        value: { ...this.pagination, limit: val, offset: 0 },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.blogFilters {
  &__title {
    font-size: 3.6rem;
    line-height: 4.5rem;
    font-weight: 600;
    padding-bottom: 3rem;

    @include rwd('tablet') {
      font-size: 3rem;
      line-height: 4rem;
    }
  }

  &__header {
    margin-bottom: 3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__sectionTitle {
    font-size: 3.6rem;
    line-height: 4.5rem;
    font-weight: 600;

    @include rwd('tablet') {
      width: 100%;
      text-align: center;
      font-size: 3rem;
      line-height: 3.8rem;
    }
  }

  &__filters {
    border-top: 1px solid $cAddLight;
    display: flex;
    padding-top: 3rem;
    margin-bottom: 4rem;
    width: calc(100% - 4rem);
    margin-left: 2rem;
  }

  &__filtersPagination {
    margin-left: auto;
  }

  &__itemsWrapper {
    position: relative;

    .loader {
      top: 100px;
    }
  }

  &__items {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -8px;
    transition: opacity $tr;

    &--loading {
      opacity: 0.6;
    }
  }

  &__item {
    margin-bottom: 4rem;
    flex: 0 0 25%;
    max-width: 25%;
    padding: 0 8px;

    &--big {
      flex: 0 0 33.33333%;
      max-width: 33.33333%;
    }

    @include rwd('large-tablet') {
      flex: 0 0 33.333%;
      max-width: 33.333%;
    }

    @include rwd('tablet') {
      flex: 0 0 50%;
      max-width: 50%;
    }

    @include rwd('small-tablet') {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }

  &__pagination {
    padding-top: 3rem;

    @include rwd('tablet') {
      padding-top: 1rem;
    }
  }
}
</style>
